/**
 * window.__agentAuthApi — passwordless login for allowlisted AI agent accounts.
 * Backed by the DEV-only `createAiAgentLoginToken` Cloud Function (hard-fails on prod).
 *
 * Playwright usage:
 *   await page.evaluate(() => window.__agentAuthApi.loginAsAiAgent('ai-gamer-1@intraction-agents.dev'))
 */
import { signInWithCustomToken, signOut } from 'firebase/auth'
import { httpsCallable } from 'firebase/functions'
import { CloudFunction } from '../../../pure-js/enums/CloudFunction'
import type {
  CreateAiAgentLoginTokenRequest,
  CreateAiAgentLoginTokenResponse
} from '../../../pure-js/types/AgentControl'
import { auth, functions } from '../libs/Firebase'

export type AgentAuthApi = {
  /** Mints a dev-only custom token for the allowlisted agent email and signs in. */
  loginAsAiAgent: (email: string) => Promise<{ userId: string }>
  logout: () => Promise<void>
  getCurrentUser: () => { uid: string; email: string | null } | null
}

export const createAgentAuthApi = (): AgentAuthApi => ({
  loginAsAiAgent: async (email: string) => {
    const callable = httpsCallable<CreateAiAgentLoginTokenRequest, CreateAiAgentLoginTokenResponse>(
      functions,
      CloudFunction.createAiAgentLoginToken
    )
    const result = await callable({ email })
    const { success, token, userId } = result.data
    if (!success || !token) throw new Error('createAiAgentLoginToken failed for ' + email)
    await signInWithCustomToken(auth, token)
    // Claims were refreshed server-side before the token was minted; force the client
    // to pick them up so org/team guards see the right tenant immediately.
    await auth.currentUser?.getIdToken(true)
    return { userId }
  },
  logout: () => signOut(auth),
  getCurrentUser: () => {
    const u = auth.currentUser
    return u ? { uid: u.uid, email: u.email } : null
  }
})
